import { BiLogIn } from "react-icons/all";
import { useColorModeValue, VStack } from "@chakra-ui/react";

import { ILoginForm } from "modules/authentication/application/types/ILoginForm";
import { loginFormValidationSchema } from "modules/authentication/application/validation/loginFormValidationSchema";
import { useLoginUser } from "modules/authentication/infrastructure/useLoginUser";

import { useIsSsoAuthenticationAllowed } from "utils/useIsSsoAuthenticationAllowed";

import {
  Form,
  IErrorField,
  SpinnerButton,
  TextField,
  TextFieldType,
} from "components/Form";
import { Icon } from "components/Icon";
import { ChakraText } from "components/ChakraText";
import { useGetThemeColors } from "ChakraTheme";
import { useLoginNotification } from "../useLoginNotification";
import { RemindPasswordLink } from "./RemindPasswordLink";
import { SsoLoginButton } from "./SsoLoginButton";

interface IProps {
  onCloseModal: () => void;
  openRemindPasswordModal: () => void;
}

const LoginForm = ({ onCloseModal, openRemindPasswordModal }: IProps) => {
  const colors = useGetThemeColors();
  const iconColor = useColorModeValue(colors.white, colors.black);
  const isSsoAuthenticationAllowed = useIsSsoAuthenticationAllowed();
  const {
    showSuccessNotification,
    showErrorNotification,
  } = useLoginNotification();
  const mutation = useLoginUser();

  const handleSubmit = async (
    model: ILoginForm,
    setCustomErrors: (errors: IErrorField[]) => void
  ) => {
    const formData = new FormData();
    Object.keys(model).forEach((key) => {
      formData.append(key, model[key as keyof typeof model].toString());
    });
    try {
      await mutation.mutateAsync(formData);
      showSuccessNotification();
      onCloseModal();
    } catch (e) {
      if (e.status === 400) {
        setCustomErrors(e.response.msg);
      }
      showErrorNotification(e.message);
    }
  };

  return (
    <Form<ILoginForm>
      onSubmit={handleSubmit}
      defaultValues={{
        email: "",
        password: "",
      }}
      validationSchema={loginFormValidationSchema}
      resetOnSubmit={false}
    >
      {() => {
        return (
          <VStack spacing={4} align="stretch">
            <TextField label="Adres email" name="email" />
            <TextField
              label="Hasło"
              name="password"
              type={TextFieldType.PASSWORD}
            />

            <RemindPasswordLink
              closeLoginModal={onCloseModal}
              openRemindPasswordModal={openRemindPasswordModal}
            />

            <VStack spacing={4} mt={8}>
              <SpinnerButton
                btnTitle={<ChakraText>Zaloguj się</ChakraText>}
                leftIcon={<Icon color={iconColor} icon={BiLogIn} />}
                id="login-btn"
                w="250px"
              />
              {isSsoAuthenticationAllowed && <SsoLoginButton />}
            </VStack>
          </VStack>
        );
      }}
    </Form>
  );
};

export { LoginForm };
